import { useState, useMemo, useEffect, useRef } from "react";

type UseDropdownProps = {
  initialItem: string;
  allItems: string[];
  onDropdownAction: (item: string) => void;
  onlyAction?: boolean;
  sort?: boolean;
};

export const useDropdown = ({
  initialItem,
  allItems,
  onDropdownAction,
  onlyAction = false,
  sort = false,
}: UseDropdownProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string>(initialItem);
  const [items, setItems] = useState<string[]>(allItems);
  const prevAllItemsRef = useRef<string[]>(allItems);
  const prevInitialItemRef = useRef<string>(initialItem);

  useEffect(() => {
    // Only update if the arrays are actually different (deep comparison)
    const arraysEqual = (a: string[], b: string[]) =>
      a.length === b.length && a.every((val, i) => val === b[i]);

    if (!arraysEqual(prevAllItemsRef.current, allItems)) {
      setItems(allItems);
      prevAllItemsRef.current = allItems;
    }
  }, [allItems]);

  // Re-sync when the parent passes a new "current" value (e.g. EventSwitcher
  // after a cache-hit event switch where the hook does not unmount).
  useEffect(() => {
    if (prevInitialItemRef.current !== initialItem) {
      setSelectedItem(initialItem);
      prevInitialItemRef.current = initialItem;
    }
  }, [initialItem]);

  const handleSelect = (item: string, changeDefault: boolean) => {
    if (changeDefault === true && onlyAction === false) {
      setItems((prevItems) => {
        const updatedItems = [...prevItems, selectedItem]
          .filter((i) => i !== item)
          .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates

        if (sort === false) return updatedItems;
        return updatedItems.sort((a, b) => {
          const numA = parseFloat(a);
          const numB = parseFloat(b);

          if (!isNaN(numA) && !isNaN(numB)) return numA - numB; // Sort numbers
          if (!isNaN(numA)) return -1; // Numbers before strings
          if (!isNaN(numB)) return 1;

          return a.localeCompare(b, undefined, { sensitivity: "base" }); // Sort strings alphabetically
        });
      });

      setSelectedItem(item);
    }
    onDropdownAction(item);
    setIsDropdownOpen(false);
  };

  const dropdownWidth = useMemo(
    () =>
      `${Math.max(...[selectedItem, ...items].map((item) => item?.length))}ch`,
    [selectedItem, items]
  );

  return {
    isDropdownOpen,
    selectedItem,
    items,
    dropdownWidth,
    setIsDropdownOpen,
    handleSelect,
  };
};
